import { useEffect, useRef, useState } from 'react';
import useAnamNudgytAgentAudioQueue from './useAnamNudgytAgentrAudioQueue';
import { isIOS, postMessage } from '../helpers/utils';
import { useSimulationContext } from '../providers/SimulationProvider';
import {
  MESSAGE_FROM,
  MESSAGE_TYPES,
  NUDGYT_AGENT_MESSAGE_TYPES,
  NUDGYT_AGENT_STOPPABLE_RESPONSE_TYPES,
  STOP_RESPONSE_GATE_FALLBACK_MS
} from '../constants/simulationConstants';
import useAudioQueue from './useAudioQueue';
import useResponseTimeout from './useResponseTimeout';
import nudgytAgentConfig from '../config/nudgytAgentConfig';
import createNudgytAgentChatSocket from '../websocket/nudgytAgentChatSocket';

function useAnamNudgytAgentClient({
  aiResponsePlaybackActiveRef,
  isVideoStreamReady,
  handleSendAudioChunk,
  handleEndAudioSequence
} = {}) {
  const [isWsConnected, setIsWsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const shouldReconnectRef = useRef(true);
  const isIntentionalCloseRef = useRef(false);
  const nudgytSessionTokenRef = useRef(null);
  const isEnableHookRef = useRef(false);
  const lastConnectedTokenRef = useRef(null);
  const deferredTokenReconnectRef = useRef(false);
  const aiResponseInFlightRef = useRef(false);
  const flushDeferredTokenReconnectRef = useRef(() => {});
  // Route currently in effect for coach audio (local playback vs. Anam).
  const isLocalAudioRouteRef = useRef(false);
  // Route that *should* apply, ignoring the mid-reply latch below.
  const shouldRouteLocalRef = useRef(false);
  /**
   * Set while a stop is pending the server's `cancelled` echo — see
   * useThreeNudgytAgentClient for why the in-flight flag cannot do this alone.
   */
  const stoppedResponseRef = useRef(false);
  const stopResponseFallbackTimeoutRef = useRef(null);

  const { enqueueChunk, enqueueEndSequence, flushQueue, initAudio } =
    useAnamNudgytAgentAudioQueue({
      handleSendAudioChunk,
      handleEndAudioSequence
    });

  // Local MP3 playback, used while the avatar (and with it the Anam session
  // that normally renders the coach's voice) is hidden.
  const { enqueueAudio, clearAudioQueue } = useAudioQueue();

  const {
    userText,
    documentIds,
    characterIds,
    userInteract,
    isAvatarHidden,
    nudgytSessionToken,
    isClientInitialized,
    setUserText,
    setIsClientInitialized,
    setDocumentIds,
    setUserInteract,
    setSelectedCharacterId,
    isStopResponseRequested,
    setIsStopResponseRequested,
    setIsUserInterrupt
  } = useSimulationContext();

  const hasSession = Boolean(nudgytSessionToken);
  nudgytSessionTokenRef.current = nudgytSessionToken;
  isEnableHookRef.current = hasSession;
  // Only once the Anam stream is actually gone — while a hide waits for the
  // coach to stop speaking, Anam still owns the audio.
  shouldRouteLocalRef.current = isAvatarHidden && !isVideoStreamReady;

  /**
   * Reopens the gate closed by a stop request, so the next reply streams
   * normally.
   */
  const clearStoppedResponseGate = () => {
    stoppedResponseRef.current = false;
    if (stopResponseFallbackTimeoutRef.current) {
      clearTimeout(stopResponseFallbackTimeoutRef.current);
      stopResponseFallbackTimeoutRef.current = null;
    }
  };

  /**
   * Interrupts the Anam persona, which is the only way to silence speech it has
   * already buffered.
   */
  const interruptAnamSpeech = () => {
    setIsUserInterrupt(true);
    if (aiResponsePlaybackActiveRef)
      aiResponsePlaybackActiveRef.current = false;
  };

  /**
   * Silences everything the coach has left to say, on every route. Used when
   * the reply itself is being abandoned rather than just skipped.
   */
  const silenceAllSpeech = () => {
    flushQueue();
    clearAudioQueue();
    interruptAnamSpeech();
  };

  /**
   * Resets the client state to default values
   * Clears talking state, user text, and facial animation data
   */
  const resetClientState = () => {
    setUserText('');
    setDocumentIds([]);
  };

  /**
   * Response timeout handler
   * Detects when Nudgyt Agent AI fails to respond within 30 seconds
   */
  const { startResponseTimeout, clearResponseTimeout } = useResponseTimeout({
    onTimeout: () => {
      resetClientState();
      // Websocket reconnect config
      aiResponseInFlightRef.current = false;
      flushDeferredTokenReconnectRef.current();
    }
  });

  // Handle websocket reconnection logic
  const handleReconnection = () => {
    reconnectAttemptsRef.current += 1;

    reconnectTimeoutRef.current = setTimeout(() => {
      connectWebSocket();
    }, nudgytAgentConfig.reconnectDelay);
  };

  /**
   * Establishes WebSocket connection to Nudgyt Agent server
   * Sets up event handlers for connection lifecycle and message handling
   */
  const connectWebSocket = () => {
    if (!isEnableHookRef.current) return;

    const accessTokenForConnection = nudgytSessionTokenRef.current;

    if (!accessTokenForConnection) {
      console.error('[Nudgyt Agent Client] Missing auth: nudgytSessionToken');
      return;
    }

    // Clear any existing reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    const ws = createNudgytAgentChatSocket({
      base: nudgytAgentConfig.baseUrl,
      sessionToken: accessTokenForConnection
    });

    if (!ws) return;

    ws.onopen = () => {
      const latestToken = nudgytSessionTokenRef.current;
      if (latestToken && latestToken !== accessTokenForConnection) {
        isIntentionalCloseRef.current = true;
        ws.close(1000, 'Superseded by newer session token');
        isIntentionalCloseRef.current = false;
        reconnectAttemptsRef.current = 0;
        if (isEnableHookRef.current && nudgytSessionTokenRef.current)
          connectWebSocket();

        return;
      }
      setIsWsConnected(true);
      lastConnectedTokenRef.current = accessTokenForConnection;
      reconnectAttemptsRef.current = 0; // Reset reconnect attempts on successful connection
      // Initialize audio context
      initAudio();
    };

    /**
     * WebSocket onmessage event handler
     * Processes incoming AI messages (text, audio, emotion, viseme data)
     */
    ws.onmessage = async (ev) => {
      let aiMessage = JSON.parse(ev.data);

      if (
        !aiResponseInFlightRef.current &&
        (aiMessage.type === NUDGYT_AGENT_MESSAGE_TYPES.TEXT_TOKEN ||
          aiMessage.type === NUDGYT_AGENT_MESSAGE_TYPES.AUDIO_RESPONSE)
      ) {
        return;
      }

      // Chunks still arriving from a reply the learner stopped — dropped
      // before they can reach Anam, local playback, or the parent.
      if (
        stoppedResponseRef.current &&
        NUDGYT_AGENT_STOPPABLE_RESPONSE_TYPES.includes(aiMessage.type)
      ) {
        return;
      }

      const validTypes = Object.values(NUDGYT_AGENT_MESSAGE_TYPES);

      if (
        typeof aiMessage !== 'object' ||
        !aiMessage.type ||
        !validTypes.includes(aiMessage.type)
      ) {
        console.warn(
          '[Nudgyt Agent Client Warning] Unknown message type:',
          aiMessage
        );
      }

      let flushDeferredAfterForward = false;

      switch (aiMessage.type) {
        case NUDGYT_AGENT_MESSAGE_TYPES.TEXT_QUERY:
          startResponseTimeout();
          break;
        case NUDGYT_AGENT_MESSAGE_TYPES.RESPONSE_START:
          aiResponseInFlightRef.current = true;
          // Ordered delivery puts every straggler from the stopped reply
          // before this point, so it is safe to reopen here.
          clearStoppedResponseGate();
          // A reply that will come back through Anam supersedes anything still
          // playing locally from before the avatar was shown — the user has
          // moved on, and leaving the tail running means two coach voices at
          // once.
          if (!isLocalAudioRouteRef.current) clearAudioQueue();
          resetClientState();
          clearResponseTimeout();
          break;
        case NUDGYT_AGENT_MESSAGE_TYPES.RESPONSE_ENDED:
          aiResponseInFlightRef.current = false;
          flushDeferredAfterForward = true;
          enqueueEndSequence();
          isLocalAudioRouteRef.current = shouldRouteLocalRef.current;
          break;
        case NUDGYT_AGENT_MESSAGE_TYPES.ERROR:
        case NUDGYT_AGENT_MESSAGE_TYPES.CANCELLED:
          aiResponseInFlightRef.current = false;
          flushDeferredAfterForward = true;
          clearStoppedResponseGate();
          handleEndAudioSequence();
          isLocalAudioRouteRef.current = shouldRouteLocalRef.current;
          break;
        case NUDGYT_AGENT_MESSAGE_TYPES.EMOTION:
          // Disable emotion for Anam video stream
          // const emotionData = aiMessage.emotion;
          // if (typeof emotionData === 'object' && emotionData)
          //   setEmotion(emotionData);
          break;
        case NUDGYT_AGENT_MESSAGE_TYPES.AUDIO_RESPONSE:
          // While hidden there is no Anam session to lip-sync the voice, so
          // play the chunk locally instead of pushing it into Anam.
          if (isLocalAudioRouteRef.current) {
            enqueueAudio({
              audio: aiMessage.audio,
              viseme: aiMessage.viseme || []
            });
            break;
          }

          if (!aiResponsePlaybackActiveRef.current)
            aiResponsePlaybackActiveRef.current = true;
          enqueueChunk(aiMessage.audio);
          break;
        default:
      }

      // Send AI message to parent application
      postMessage({
        type: MESSAGE_TYPES.CHAT,
        payload: { from: MESSAGE_FROM.AI, ...aiMessage }
      });

      if (flushDeferredAfterForward)
        queueMicrotask(() => {
          flushDeferredTokenReconnectRef.current();
        });
    };

    /**
     * WebSocket onerror event handler
     * Handles connection errors, clears timeout, and notifies parent application
     */
    ws.onerror = (error) => {
      console.error('[Nudgyt Agent Client Error] WebSocket error:', error);
      clearResponseTimeout();
    };

    /**
     * WebSocket onclose event handler
     * Handles connection closure and manages reconnection logic
     * Reconnection conditions:
     * 1. Not an intentional close
     * 2. Reconnection flag is isEnable
     * 3. Haven't exceeded max reconnection attempts
     */
    ws.onclose = (event) => {
      const closedSocket = event.target;
      // Ignore closes after session token refresh socket
      if (closedSocket !== wsRef.current) return;

      console.warn('[Nudgyt Agent WebSocket Close]', event);

      setIsWsConnected(false);
      wsRef.current = null;

      if (
        !isIntentionalCloseRef.current &&
        shouldReconnectRef.current &&
        reconnectAttemptsRef.current < nudgytAgentConfig.maxReconnectAttempts
      ) {
        handleReconnection();
      } else if (
        reconnectAttemptsRef.current >= nudgytAgentConfig.maxReconnectAttempts
      ) {
        console.error(
          '[Nudgyt Agent Client Error] Max reconnection attempts reached'
        );
        postMessage({
          type: MESSAGE_TYPES.CHAT,
          error: 'Connection failed'
        });
      } else if (!isIntentionalCloseRef.current) {
        console.error(
          '[Nudgyt Agent Client Error] Nudgyt Agent webSocket disconnected'
        );
      }

      // Reset intentional close flag
      isIntentionalCloseRef.current = false;
    };

    // Set WebSocket reference
    wsRef.current = ws;
  };

  /**
   * Applies a deferred session-token rotation after the current AI turn ends.
   * When nudgytSessionToken changes mid-reply, rotation is deferred until
   * response_ended, error, cancelled, or response timeout. If a newer token
   * is still pending, closes the live socket and reconnects with it.
   */
  const flushDeferredTokenReconnectIfNeeded = () => {
    if (!deferredTokenReconnectRef.current) return;
    if (!isEnableHookRef.current) {
      deferredTokenReconnectRef.current = false;
      return;
    }
    const desiredToken = nudgytSessionTokenRef.current;
    if (!desiredToken) {
      deferredTokenReconnectRef.current = false;
      return;
    }
    if (lastConnectedTokenRef.current === desiredToken) {
      deferredTokenReconnectRef.current = false;
      return;
    }
    deferredTokenReconnectRef.current = false;
    isIntentionalCloseRef.current = true;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    const socket = wsRef.current;
    if (
      socket &&
      (socket.readyState === WebSocket.OPEN ||
        socket.readyState === WebSocket.CONNECTING)
    ) {
      socket.close(1000, 'Session token rotated after AI response');
    }
    isIntentionalCloseRef.current = false;
    reconnectAttemptsRef.current = 0;
    connectWebSocket();
  };

  flushDeferredTokenReconnectRef.current = flushDeferredTokenReconnectIfNeeded;

  /**
   * Effect: Switch the coach voice between Anam and local playback, following
   * the Anam stream itself rather than the toggle — a hide waits for the coach
   * to finish speaking, and until then Anam must keep receiving the audio or
   * the two paths would talk over each other.
   *
   * A reply already in flight keeps its current path until it ends, so a single
   * reply is never split across both.
   */
  useEffect(() => {
    if (shouldRouteLocalRef.current) {
      isLocalAudioRouteRef.current = true;
      return;
    }

    if (!aiResponseInFlightRef.current) isLocalAudioRouteRef.current = false;
  }, [isAvatarHidden, isVideoStreamReady]);

  useEffect(() => {
    // The avatar being hidden means no video stream will ever become ready,
    // so gate on it too — otherwise hiding before the first Anam connect would
    // leave the client uninitialized and the chat input disabled forever.
    if (
      isWsConnected &&
      !isClientInitialized &&
      (isVideoStreamReady || isAvatarHidden)
    ) {
      setIsClientInitialized(true);

      // If the client is initialized on iOS, we don't need to send the INITIALIZED message
      // To prevent user to send message before tapping on the screen
      if (isIOS()) return;

      postMessage({ type: MESSAGE_TYPES.INITIALIZED });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWsConnected, isClientInitialized, isVideoStreamReady, isAvatarHidden]);

  /**
   * Effect: Enable / disable hook — teardown when disabled or unmounting.
   */
  useEffect(() => {
    if (!isEnableHookRef.current) {
      deferredTokenReconnectRef.current = false;
      aiResponseInFlightRef.current = false;
      lastConnectedTokenRef.current = null;
      return;
    }

    shouldReconnectRef.current = true;
    isIntentionalCloseRef.current = false;
    reconnectAttemptsRef.current = 0;

    return () => {
      shouldReconnectRef.current = false;
      isIntentionalCloseRef.current = true;
      deferredTokenReconnectRef.current = false;
      aiResponseInFlightRef.current = false;
      lastConnectedTokenRef.current = null;
      stoppedResponseRef.current = false;

      if (stopResponseFallbackTimeoutRef.current) {
        clearTimeout(stopResponseFallbackTimeoutRef.current);
        stopResponseFallbackTimeoutRef.current = null;
      }

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      const socket = wsRef.current;
      if (
        socket &&
        (socket.readyState === WebSocket.OPEN ||
          socket.readyState === WebSocket.CONNECTING)
      )
        socket.close(1000, 'Component unmounting');
    };
  }, [hasSession]);

  /**
   * Effect: Connect or rotate WebSocket when session token changes.
   * While an AI reply is in flight (response_start without response_ended), defer
   * closing the socket until response_ended so the parent can finalize transcript.
   */
  useEffect(() => {
    if (!isEnableHookRef.current || !nudgytSessionToken) return;

    const socket = wsRef.current;
    const hasLiveSocket =
      socket &&
      (socket.readyState === WebSocket.OPEN ||
        socket.readyState === WebSocket.CONNECTING);

    if (!hasLiveSocket) {
      connectWebSocket();
      return;
    }

    if (socket.readyState === WebSocket.CONNECTING) return;

    if (lastConnectedTokenRef.current === nudgytSessionToken) return;

    if (aiResponseInFlightRef.current) {
      deferredTokenReconnectRef.current = true;
      return;
    }

    isIntentionalCloseRef.current = true;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (
      socket &&
      (socket.readyState === WebSocket.OPEN ||
        socket.readyState === WebSocket.CONNECTING)
    ) {
      socket.close(1000, 'Session token rotated');
    }
    isIntentionalCloseRef.current = false;
    reconnectAttemptsRef.current = 0;
    connectWebSocket();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- connectWebSocket uses refs
  }, [nudgytSessionToken]);

  useEffect(() => {
    if (!isEnableHookRef.current) return;

    const onParentMessage = (event) => {
      let data = event.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch {
          return;
        }
      }
      if (
        data?.type === MESSAGE_TYPES.CHAT &&
        data?.payload?.type === NUDGYT_AGENT_MESSAGE_TYPES.STOP_AUDIO
      ) {
        setUserText('');
        setDocumentIds([]);
        // The parent sends this when the reply must not be heard at all, so
        // everything goes — not just the reply being spoken.
        silenceAllSpeech();
      }
    };

    window.addEventListener('message', onParentMessage);
    return () => window.removeEventListener('message', onParentMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSession, setUserText, setDocumentIds, setIsUserInterrupt]);

  useEffect(() => {
    if (!isEnableHookRef.current || !isStopResponseRequested) return;

    resetClientState();
    silenceAllSpeech();

    clearResponseTimeout();
    aiResponseInFlightRef.current = false;
    // Stop ends the reply as far as routing is concerned, exactly as
    // response_ended and cancelled do. Waiting for the echo instead would keep
    // the coach on local audio after the learner has revealed the avatar.
    isLocalAudioRouteRef.current = shouldRouteLocalRef.current;

    stoppedResponseRef.current = true;
    // Safety valve for an agent that never echoes `cancelled` — without it the
    // gate would stay shut and mute every later reply.
    if (stopResponseFallbackTimeoutRef.current)
      clearTimeout(stopResponseFallbackTimeoutRef.current);
    stopResponseFallbackTimeoutRef.current = setTimeout(() => {
      stopResponseFallbackTimeoutRef.current = null;
      stoppedResponseRef.current = false;
      isLocalAudioRouteRef.current = shouldRouteLocalRef.current;
    }, STOP_RESPONSE_GATE_FALLBACK_MS);

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({ type: NUDGYT_AGENT_MESSAGE_TYPES.STOP_RESPONSE })
      );
    }

    setIsStopResponseRequested(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStopResponseRequested, setIsStopResponseRequested]);

  /**
   * Effect: Send user text to Nudgyt Agent
   * Triggers when user submits new text message
   * Starts timeout timer and sends text query to AI
   *
   * Nothing here has to interrupt the coach: the parent locks the chat for as
   * long as the coach is speaking, so a new message can only arrive once the
   * previous answer is silent — by having finished, or by Stop.
   */
  useEffect(() => {
    if (!isEnableHookRef.current) return;

    if (
      (userText || documentIds.length > 0) &&
      isWsConnected &&
      wsRef.current
    ) {
      const userPayload = {
        type: NUDGYT_AGENT_MESSAGE_TYPES.TEXT_QUERY,
        ...(userText && { text: userText }),
        ...(documentIds.length > 0 && { document_ids: documentIds }),
        local_time: new Date().toISOString().slice(0, 19).replace('T', ' '),
        mute_audio: false
      };

      // Send user text to Nudgyt Agent and start timeout timer
      wsRef.current.send(JSON.stringify(userPayload));
      startResponseTimeout();
      setUserText('');
      setDocumentIds([]);
    }

    //  AI conversation initiation settings including snooze configuration.
    if (userInteract && isWsConnected && wsRef.current) {
      wsRef.current.send(JSON.stringify(userInteract));
      setSelectedCharacterId(characterIds[0]);
      setUserInteract(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSession, userText, isWsConnected, userInteract, startResponseTimeout]);
}

export default useAnamNudgytAgentClient;
